import React, { useCallback, useEffect, useState } from "react";
import { Search } from "lucide-react";
import { Callout, Input, NativeSelect, PanelButton } from "../ui";
import Shimmer from "../Common/Shimmer";
import { adminSearchResources } from "../../utils/smartLearning";

const titleCase = (s = "") => s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, " ");

/** The filters each kind offers, read from the search's own facets. */
const FILTERS = {
  codingChallenge: [
    { key: "difficulty", facet: "difficulties", all: "All difficulties" },
    { key: "category", facet: "categories", all: "All categories" },
    { key: "label", facet: "labels", all: "All tags" },
  ],
  quiz: [
    { key: "status", facet: "statuses", all: "All statuses", word: titleCase },
    { key: "mode", facet: "modes", all: "All modes", word: titleCase },
  ],
  assignment: [
    { key: "batchId", facet: "batches", all: "All batches" },
    { key: "status", facet: "statuses", all: "All statuses", word: titleCase },
  ],
};

/**
 * Search the institute's existing resources of one kind and pick one.
 *
 * Props: kind, meta (KIND_META entry), courseId, batchId, onPick({ _id, title, kind, contentType })
 */
const PickExisting = ({ kind, meta, courseId, batchId, onPick }) => {
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({});
  const [items, setItems] = useState([]);
  const [facets, setFacets] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const Icon = meta.Icon;

  const search = useCallback(async (term) => {
    setLoading(true);
    setError("");
    try {
      const { items: rows, facets: f } = await adminSearchResources({
        kind,
        q: term,
        courseId,
        batchId: kind === "assignment" ? filters.batchId || batchId : batchId,
        limit: 30,
        status: filters.status || undefined,
        mode: filters.mode || undefined,
        difficulty: filters.difficulty || undefined,
        category: filters.category || undefined,
        label: filters.label || undefined,
      });
      setItems(rows || []);
      setFacets((prev) => (Object.keys(f || {}).length ? f : prev));
    } catch (err) {
      setError(err?.response?.data?.message || "The search did not work. Try again.");
    } finally {
      setLoading(false);
    }
  }, [kind, courseId, batchId, filters]);

  useEffect(() => {
    const t = setTimeout(() => search(q.trim()), q ? 250 : 0);
    return () => clearTimeout(t);
  }, [q, search]);

  const filterDefs = FILTERS[kind] || [];

  return (
    <div className="tw-flex tw-flex-col tw-gap-3">
      <Input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={`Search ${meta.plural}…`}
        aria-label={`Search ${meta.plural}`}
        leading={<Search size={15} aria-hidden="true" />}
        autoFocus
      />
      {filterDefs.length > 0 && (
        <div className="tw-grid tw-gap-2 min-[576px]:tw-grid-cols-2">
          {filterDefs.map((f) => (
            <NativeSelect
              key={f.key}
              size="sm"
              aria-label={f.all}
              value={filters[f.key] || ""}
              onChange={(e) => setFilters((prev) => ({ ...prev, [f.key]: e.target.value }))}
            >
              <option value="">{f.all}</option>
              {(facets[f.facet] || []).map((opt) => {
                const value = typeof opt === "object" ? opt._id : opt;
                const label = typeof opt === "object" ? opt.name : (f.word ? f.word(opt) : opt);
                return <option key={value} value={value}>{label}</option>;
              })}
            </NativeSelect>
          ))}
        </div>
      )}

      {error && <Callout tone="danger" role="alert">{error}</Callout>}

      <div aria-live="polite" className="tw-flex tw-flex-col tw-gap-2">
        {loading && <Shimmer height={48} count={3} />}
        {!loading && !items.length && (
          <p className="tw-m-0 tw-rounded-md tw-bg-surface-sunken tw-px-3 tw-py-4 tw-text-center tw-text-[0.8125rem] tw-text-content-muted">
            No {meta.plural} found{q ? ` for “${q}”` : ""}.
          </p>
        )}
        {!loading && items.map((it) => (
          <PanelButton
            key={it._id}
            onClick={() => onPick({ _id: it._id, title: it.title, kind: it.kind || kind, contentType: it.contentType || "" })}
          >
            <span className="tw-flex tw-h-8 tw-w-8 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-md tw-bg-primary/10 tw-text-primary">
              <Icon size={16} aria-hidden="true" />
            </span>
            <span className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col">
              <span className="tw-truncate tw-text-sm tw-font-medium tw-text-content">{it.title}</span>
              {it.subtitle && <span className="tw-truncate tw-text-xs tw-text-content-muted">{it.subtitle}</span>}
            </span>
          </PanelButton>
        ))}
      </div>
    </div>
  );
};

export default PickExisting;
